import { Point } from "../domain/types";
import { TooltipItem } from "chart.js";
import genMinValue from "../utils/genMinValue";

function getOptions(areaType: string, minValue: number, maxValue: number, transitionPoint: Point, maxTicksLimit: number, maintainAspectRatio: boolean, firstY: any) {
  const differenceVal = (maxValue - minValue) * 0.15;
  return {
    animation: {
      duration: 0,
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    responsive: true,
    maintainAspectRatio: maintainAspectRatio,
    scales: {
      x: {
        type: "time" as const,
        time: {
          unit: "day" as const,
        },
        distribution: 'series',
        grid: {
          display: false,
        },
        ticks: {
          color: "#f1f5f9",
          font: {
            size: window.innerWidth < 1500 ? 9 : (window.innerWidth < 1800 ? 10 : 13),
          },
          maxTicksLimit,
        },
      },
      y: {
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.2)',
          tickBorderDash: [2, 2],
          tickLength: 8,
        },
        ticks: {
          color: "#f1f5f9",
          crossAlign: "far" as const,
          font: {
            size: window.innerWidth < 1500 ? 9 : (window.innerWidth < 1800 ? 10 : 13),
          },
          callback: function (value: any) {
            if (value < 50 && value >= 10) {
              return value.toFixed(1);
            }
            if (value > 50) {
              return Math.round(value);
            } else {
              return value.toFixed(2);
            }
          },
        },
        min: genMinValue(minValue, differenceVal),
        max: maxValue + differenceVal,
      },
    },
    elements: {
      line: {
        tension: 0,
        borderWidth: 2,
      },
    },
    plugins: {
      datalabels: {
        // hide datalabels for all datasets
        display: false,
      },
      hover: {
        mode: 'index' as const,
        animationDuration: 0,
      },
      annotation: {
        annotations: {
          breakPoint: {
            type: 'point' as const,
            xScaleID: 'x' as const,
            yScaleID: 'y' as const,
            xValue: transitionPoint.x,
            yValue: transitionPoint.y,
            backgroundColor: '#65F4CD',
            radius: 4,
            label: {
              enabled: true,
              content: 'Break Point' as const,
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: "nearest" as const,
        position: "nearest" as const,
        bodyFont: {
          size: 11,
        },
        titleFont: {
          size: 11,
        },
        borderColor: '#65F4CD',
        borderWidth: 1,
        backgroundColor: "white",
        titleColor: "#232323",
        bodyColor: "#333333",
        axis: "x" as const,
        intersect: false,
        filter: function (tooltipItem: any) {
          return tooltipItem.datasetIndex < 2;
        },
        callbacks: {
          label: function (tooltipItem: TooltipItem<"line">) {
            const { label } = tooltipItem.dataset;
            const tooltipNames: { [key: string]: string } = {
              'Tag Value': 'Hist. Data',
              'Prediction Value': 'Prediction',
              'minSet': 'Min',
              'maxSet': 'Max',
            };
            if (label) {
              const tooltipName = tooltipNames[label];
              return `${tooltipName} ${tooltipItem.formattedValue}`;
            }
            return;
          }
        }
      },
      legend: {
        display: false,
      },
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgba(101, 244, 205, 0.2)",
            borderColor: "#65F4CD",
            borderWidth: 1,
          },
          wheel: {
            enabled: true,
            modifierKey: "ctrl" as const,
          },
          mode: "x" as const,
          limits: {
            x: {
              minRange: 1000 * 60 * 60 * 24 * 1,
            },
          },
          onZoom: ({ chart }: any) => {
            const xAxis = chart.scales.x;
            const range = xAxis.max - xAxis.min;
            const maxRangeMonth = 1000 * 60 * 60 * 24 * 30;
            const maxRangeYear = 1000 * 60 * 60 * 24 * 365;
            const maxRange = areaType === 'yearly' ? maxRangeYear : maxRangeMonth;
            if (range > maxRange) {
              // Reset to max allowed zoom out
              const center = (xAxis.max + xAxis.min) / 2;
              xAxis.options.min = center - maxRange / 2;
              xAxis.options.max = center + maxRange / 2;
              chart.update();
            }
          },
        },
      },
    },
  };
}

export default getOptions;